/**
 * @file AppContext.js — État global de l'application pour les cercles et le mode de vue.
 * Suit les cercles de l'utilisateur, le cercle actuellement actif et
 * le mode de vue courant (utilisateur / admin / gestionnaire de site).
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

const AppContext = createContext(null);

/**
 * Fournit l'état des cercles et du mode de vue à l'arbre de composants.
 * Récupère les cercles de l'utilisateur à la connexion et les réinitialise à la déconnexion.
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement}
 */
export function AppProvider({ children }) {
  const { user } = useAuth();
  const [circles, setCircles] = useState([]);
  const [_siteCircles, setSiteCircles] = useState([]);
  const [activeCircle, setActiveCircleState] = useState(null);
  const [activeCircleRole, setActiveCircleRole] = useState(null);
  const [currentView, setCurrentView] = useState('user');  // 'user' | 'admin' | 'site'
  const [loading, setLoading] = useState(true);

  // Dérive les cercles admin et les vues auxquelles l'utilisateur a accès
  const adminCircles = circles.filter(c => c.role === 'CIRCLE_ADMIN');
  // Cercles où l'utilisateur peut gérer les événements (CIRCLE_ADMIN ou is_event_admin)
  const eventAdminCircles = circles.filter(c => c.role === 'CIRCLE_ADMIN' || c.is_event_admin);
  const adminViewCircles = useMemo(() => eventAdminCircles, [eventAdminCircles]);
  const availableViews = useMemo(() => {
    const views = ['user'];
    if (adminViewCircles.length > 0) views.push('admin');
    if (user?.is_site_manager) views.push('site');
    return views;
  }, [adminViewCircles.length, user?.is_site_manager]);

  /** Récupère la liste des cercles auxquels l'utilisateur courant appartient. */
  const refreshCircles = useCallback(async () => {
    try {
      const res = await api.get('/circles/mine/');
      setCircles(res.data.circles || []);
    } catch {
      setCircles([]);
    }
  }, []);

  /** Récupère tous les cercles pour les gestionnaires du site. */
  const refreshSiteCircles = useCallback(async () => {
    if (!user?.is_site_manager) {
      setSiteCircles([]);
      return;
    }
    try {
      const res = await api.get('/circles/?per_page=200');
      setSiteCircles(res.data.circles || []);
    } catch {
      setSiteCircles([]);
    }
  }, [user?.is_site_manager]);

  /** Récupère le cercle actif côté serveur et son rôle pour l'utilisateur courant. */
  const refreshActiveCircle = useCallback(async () => {
    try {
      const res = await api.get('/circles/active/');
      setActiveCircleState(res.data.circle);
      setActiveCircleRole(res.data.role);
    } catch {
      setActiveCircleState(null);
      setActiveCircleRole(null);
    }
  }, []);

  /** Définit le cercle actif (persisté côté serveur). Passer null pour désélectionner. */
  const setActiveCircle = useCallback(async (circleId) => {
    try {
      const res = await api.post('/circles/active/', { circle_id: circleId || null });
      setActiveCircleState(res.data.circle);
      setActiveCircleRole(res.data.role);
    } catch (e) {
      console.error('Failed to set active circle', e);
    }
  }, []);

  // Charge les cercles et le cercle actif quand l'utilisateur se connecte ; réinitialise à la déconnexion
  useEffect(() => {
    if (user === null) {
      setLoading(true);
      return;
    }

    if (user && user !== false) {
      setLoading(true);
      Promise.all([
        refreshCircles(),
        refreshActiveCircle(),
        refreshSiteCircles(),
      ]).finally(() => setLoading(false));
    } else {
      setCircles([]);
      setSiteCircles([]);
      setActiveCircleState(null);
      setActiveCircleRole(null);
      setCurrentView('user');
      setLoading(false);
    }
  }, [user, refreshCircles, refreshActiveCircle, refreshSiteCircles]);

  // Revient à la vue 'user' si la vue courante n'est plus disponible
  useEffect(() => {
    if (!availableViews.includes(currentView)) {
      setCurrentView('user');
    }
  }, [availableViews, currentView]);

  return (
    <AppContext.Provider value={{
      circles, adminCircles, eventAdminCircles, adminViewCircles, activeCircle, activeCircleRole,
      currentView, setCurrentView, availableViews,
      setActiveCircle, refreshCircles, refreshActiveCircle, refreshSiteCircles, loading,
    }}>
      {children}
    </AppContext.Provider>
  );
}

/**
 * Hook pour accéder à l'état global de l'application (cercles, cercle actif, mode de vue).
 * Doit être utilisé à l'intérieur d'un AppProvider.
 * @returns {{ circles: Array, adminCircles: Array, activeCircle: Object|null, activeCircleRole: string|null, currentView: string, setCurrentView: Function, availableViews: string[], setActiveCircle: Function, refreshCircles: Function, refreshActiveCircle: Function, loading: boolean }}
 */
export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be inside AppProvider');
  return ctx;
}
